import React, { useState, useEffect } from 'react';
import Toast from './Toast';
import NoticeModal from './NoticeModal';

const Symbols = ({ 
  onCreateSymbol, 
  onUpdateSymbol,
  onGetSymbolList, 
  onGetSymbolListAll, 
  onDeleteSymbol, 
  onGetUsersList,
  onGetTradingAccountList,
  currentUser 
}) => {
  const [symbols, setSymbols] = useState([]);
  const [users, setUsers] = useState([]);
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [symbolToDelete, setSymbolToDelete] = useState(null);
  const [toast, setToast] = useState(null); // { type: 'success'|'error'|'info', message: string }
  const [notice, setNotice] = useState({ open: false, type: 'success', title: '', message: '' });
  const [limits, setLimits] = useState(null); // { minQty, maxLeverage }
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStrategy, setFilterStrategy] = useState('all');
  const [filterMarket, setFilterMarket] = useState('all');
  const [selectedSymbols, setSelectedSymbols] = useState([]); // stores userSymbolId values
  const [viewMode, setViewMode] = useState('user');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [symbolModeList, setSymbolModeList] = useState([]);
  const [selectedSymbolForUsers, setSelectedSymbolForUsers] = useState('');
  const [usersForSymbol, setUsersForSymbol] = useState([]);

  const [formData, setFormData] = useState({
    userSymbolId: '',
    userId: '',
    symbolName: '',
    accountId: '',
    strategyId: 5, // Buy Sell
    initialSizeInUsdt: 10,
    leverage: 1000,
    startTime: 20,
    endTime: 10,
    firstTakeProfitPer: 3,
    secondTakeProfitPer: 5,
    stopLoss: 2,
    market: 1, // Binance
    env: 'L', // Live
    maxLot: 55,
    forexType: 3 // Crypto
  });

  useEffect(() => {
    console.log('Symbols useEffect called');
    console.log('Available props:', { onGetUsersList, onGetTradingAccountList, currentUser });

    // Default selected user to current user if empty
    if (!selectedUserId && currentUser?.userId) {
      setSelectedUserId(currentUser.userId);
    }

    // Load users once and accounts when user changes
    loadUsers();
    if (selectedUserId) {
      loadTradingAccounts(selectedUserId);
    } else {
      setTradingAccounts([]);
    }

    loadSymbols();
  }, [currentUser, selectedUserId, selectedAccountId]);
  useEffect(() => {
    const loadUniqueSymbols = async () => {
      try {
        const api = (await import('../services/api')).default;
        const res = await api.getUniqueSymbols();
        if (Array.isArray(res)) setSymbolModeList(res);
      } catch (e) {}
    };
    if (viewMode === 'symbol' && symbolModeList.length === 0) {
      loadUniqueSymbols();
    }
  }, [viewMode, symbolModeList.length]);

  const loadUsersBySymbol = async (sym) => {
    try {
      const api = (await import('../services/api')).default;
      const res = await api.getUsersBySymbol(sym);
      if (Array.isArray(res)) setUsersForSymbol(res);
    } catch (e) {}
  };


  const loadSymbols = async () => {
    setLoading(true);
    try {
      if (!selectedAccountId) {
        setSymbols([]);
        return;
      }

      console.log('Loading symbols for accountId:', selectedAccountId);
      const result = await onGetSymbolListAll({ accountId: selectedAccountId });

      if (result.success) {
        setSymbols(result.data);
      } else {
        console.error('Failed to load symbols:', result.error);
      }
    } catch (error) {
      console.error('Error loading symbols:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      console.log('Loading users for symbols...');
      console.log('onGetUsersList function:', onGetUsersList);
      
      if (!onGetUsersList) {
        console.error('onGetUsersList function is not available');
        return;
      }
      
      const result = await onGetUsersList();
      console.log('Users result for symbols:', result);
      
      if (result && result.success) {
        setUsers(result.data);
        console.log('Users loaded successfully for symbols:', result.data.length);
      } else {
        console.error('Failed to load users for symbols:', result?.error || 'Unknown error');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users for symbols:', error);
      setUsers([]);
    }
  };

  const loadTradingAccounts = async (userId) => {
    if (!userId) {
      setTradingAccounts([]);
      return;
    }
    
    try {
      console.log('Loading trading accounts for user:', userId);
      const result = await onGetTradingAccountList({ userId });
      console.log('Trading accounts result:', result);
      if (result.success) {
        setTradingAccounts(result.data);
        console.log('Trading accounts loaded successfully:', result.data.length);
      } else {
        console.error('Failed to load trading accounts:', result.error);
        setTradingAccounts([]);
      }
    } catch (error) {
      console.error('Error loading trading accounts:', error);
      setTradingAccounts([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: type === 'number' ? parseFloat(value) || 0 : value
      };
      
      // If user is changed, reset accountId and load trading accounts for the new user
      if (name === 'userId') {
        newFormData.accountId = '';
        loadTradingAccounts(value);
      }
      
      return newFormData;
    });
  };

  const resetForm = () => {
    setFormData({
      userSymbolId: '',
      userId: '',
      symbolName: '',
      accountId: '',
      strategyId: 5, // Buy Sell
      initialSizeInUsdt: 10,
      leverage: 1000,
      startTime: 20,
      endTime: 10,
      firstTakeProfitPer: 3,
      secondTakeProfitPer: 5,
      stopLoss: 2,
      market: 1, // Binance
      env: 'L', // Live
      maxLot: 55,
      forexType: 3 // Crypto
    });
    setTradingAccounts([]);
    setShowCreateForm(false);
    setEditingSymbol(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Pre-submit validation against limits (Binance)
      if (formData.market === 1 && formData.symbolName) {
        // Validate leverage
        if (limits?.maxLeverage && Number(formData.leverage) > Number(limits.maxLeverage)) {
          setNotice({ open: true, type: 'error', title: 'Invalid Leverage', message: `Max leverage for ${formData.symbolName.toUpperCase()} is ${limits.maxLeverage}.` });
          setLoading(false);
          return;
        }

        // Validate minimum notional via minQty * price
        if (limits?.minQty) {
          try {
            const sym = formData.symbolName.toUpperCase();
            const resp = await fetch(`https://fapi.binance.com/fapi/v1/ticker/price?symbol=${encodeURIComponent(sym)}`);
            const data = await resp.json();
            const price = parseFloat(data.price);
            if (!isNaN(price)) {
              const requiredNotional = Number(limits.minQty) * price;
              if (Number(formData.initialSizeInUsdt) < requiredNotional) {
                setNotice({ open: true, type: 'error', title: 'Order Too Small', message: `Minimum notional for ${sym} is ~${requiredNotional.toFixed(2)} USDT (minQty ${limits.minQty} × price ${price.toFixed(4)}). Increase Initial Size (USDT).` });
                setLoading(false);
                return;
              }
            }
          } catch (err) {
            // If price fetch fails, skip notional validation and proceed
          }
        }
      }

      // Use update API if editing, create API if creating new
      const result = editingSymbol 
        ? await onUpdateSymbol(formData)
        : await onCreateSymbol(formData);

      if (result.success) {
        resetForm();
        loadSymbols();
        setNotice({ open: true, type: 'success', title: 'Success', message: editingSymbol ? 'Symbol updated successfully!' : 'Symbol created successfully!' });
      } else {
        setNotice({ open: true, type: 'error', title: 'Error', message: `${result.error}` });
      }
    } catch (error) {
      console.error('Error saving symbol:', error);
      setNotice({ open: true, type: 'error', title: 'Error', message: 'Failed to save symbol. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (symbol) => {
    setSymbolToDelete(symbol);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!symbolToDelete) return;
    
    setLoading(true);
    try {
      const result = await onDeleteSymbol({
        userSymbolId: symbolToDelete.userSymbolId
      });

      if (result.success) {
        loadSymbols();
        setShowDeleteConfirm(false);
        setSymbolToDelete(null);
        setNotice({ open: true, type: 'success', title: 'Deleted', message: 'Symbol deleted successfully.' });
      } else {
        setNotice({ open: true, type: 'error', title: 'Error', message: `${result.error}` });
      }
    } catch (error) {
      console.error('Error deleting symbol:', error);
      setNotice({ open: true, type: 'error', title: 'Error', message: 'Failed to delete symbol. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedSymbols.length === 0) {
      setNotice({ open: true, type: 'info', title: 'Notice', message: 'Please select symbols first.' });
      return;
    }

    setLoading(true);
    try {
      for (const symbolId of selectedSymbols) {
        const symbol = symbols.find(s => s.userSymbolId === symbolId);
        if (symbol) {
          await onDeleteSymbol({ userSymbolId: symbol.userSymbolId });
        }
      }
      
      setSelectedSymbols([]);
      loadSymbols();
    } catch (error) {
      console.error('Error performing bulk action:', error);
      setNotice({ open: true, type: 'error', title: 'Error', message: 'Failed to perform bulk action. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSymbol = (symbolId) => {
    setSelectedSymbols(prev => 
      prev.includes(symbolId) 
        ? prev.filter(id => id !== symbolId)
        : [...prev, symbolId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSymbols.length === filteredSymbols.length) {
      setSelectedSymbols([]);
    } else {
      setSelectedSymbols(filteredSymbols.map(symbol => symbol.userSymbolId));
    }
  };

  const getStrategyName = (strategyId) => {
    switch (strategyId) {
      case 1: return 'HHLL';
      case 2: return 'Super Trend';
      case 3: return 'Manual';
      case 4: return 'EMA';
      case 5: return 'Buy Sell';
      default: return 'Unknown';
    }
  };

  const getMarketName = (market) => {
    switch (market) {
      case 1: return 'Binance';
      case 2: return 'FXCM';
      case 3: return 'MT5';
      case 4: return 'cTrader';
      default: return 'Unknown';
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.userId === userId);
    return user ? `${user.userFirstName} ${user.userLastName}` : 'Unknown User';
  };

  const getTradingAccountName = (accountId) => {
    const account = tradingAccounts.find(a => a.accountId === accountId);
    return account ? account.accLoginName : 'Unknown Account';
  };

  const handleUserSelectionChange = (userId) => {
    console.log('User selection changed to:', userId);
    setSelectedUserId(userId);
    setSelectedAccountId('');
    // Accounts and symbols reload via useEffect
  };

  const handleAccountSelectionChange = (accountId) => {
    console.log('Account selection changed to:', accountId);
    setSelectedAccountId(accountId);
    // Symbols reload via useEffect
  };

  const handleEdit = (symbol) => {
    console.log('Editing symbol:', symbol);
    setEditingSymbol(symbol);
    setFormData({
      userSymbolId: symbol.userSymbolId, // Add this for update operations
      userId: symbol.userId,
      symbolName: symbol.symbolName,
      accountId: symbol.accountId,
      strategyId: symbol.strategyId,
      initialSizeInUsdt: symbol.initialSizeInUsdt,
      leverage: symbol.leverage,
      startTime: symbol.startTime,
      endTime: symbol.endTime,
      firstTakeProfitPer: symbol.firstTakeProfitPer,
      secondTakeProfitPer: symbol.secondTakeProfitPer,
      stopLoss: symbol.stopLoss,
      market: symbol.market,
      env: symbol.env,
      maxLot: symbol.maxLot,
      forexType: symbol.forexType
    });
    setShowCreateForm(true);
    
    // Load trading accounts for the selected user
    if (symbol.userId) {
      loadTradingAccounts(symbol.userId);
    }
  };

  const getStrategyColor = (strategyId) => {
    switch (strategyId) {
      case 1: return 'text-blue-400';
      case 2: return 'text-green-400';
      case 3: return 'text-yellow-400';
      case 4: return 'text-purple-400';
      case 5: return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const filteredSymbols = symbols.filter(symbol => {
    const matchesSearch = searchTerm === '' || 
      symbol.symbolName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStrategy = filterStrategy === 'all' || symbol.strategyId === parseInt(filterStrategy);
    const matchesMarket = filterMarket === 'all' || symbol.market === parseInt(filterMarket);
    
    return matchesSearch && matchesStrategy && matchesMarket;
  });

  return (
    <div className="space-y-6">
      <NoticeModal
        open={notice.open}
        type={notice.type}
        title={notice.title}
        message={notice.message}
        onClose={() => setNotice(prev => ({ ...prev, open: false }))}
        autoCloseMs={0}
      />
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-white">Trading Symbols</h3>
          <p className="text-dark-400 text-sm mt-1">Manage your trading symbols and strategies</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-3 bg-dark-800 border border-dark-700 rounded-lg px-3 py-2">
            <label className="text-dark-300 text-sm font-medium">Mode:</label>
            <label className="flex items-center gap-1 text-sm text-dark-200">
              <input type="radio" name="viewMode" checked={viewMode==='user'} onChange={()=>setViewMode('user')} /> Select by User
            </label>
            <label className="flex items-center gap-1 text-sm text-dark-2 00">
              <input type="radio" name="viewMode" checked={viewMode==='symbol'} onChange={()=>setViewMode('symbol')} /> Select by Symbol
            </label>
          </div>
          {viewMode==='user' ? (
            <>
              {/* User Selection */}
              <div className="flex items-center gap-2">
                <label className="text-dark-300 text-sm font-medium">User:</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => handleUserSelectionChange(e.target.value)}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm min-w-[220px]"
                >
                  <option value="">Select a user...</option>
                  {users.map((user) => (
                    <option key={user.userId} value={user.userId}>
                      {user.userFirstName} {user.userLastName} ({user.eMailId})
                    </option>
                  ))}
                </select>
              </div>

              {/* Trading Account Selection */}
              <div className="flex items-center gap-2">
                <label className="text-dark-300 text-sm font-medium">Trading Account:</label>
                <select
                  value={selectedAccountId}
                  onChange={(e) => handleAccountSelectionChange(e.target.value)}
                  disabled={!selectedUserId}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm min-w-[240px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">{selectedUserId ? 'All accounts of selected user' : 'Select a user first...'}</option>
                  {tradingAccounts.map((account) => (
                    <option key={account.accountId} value={account.accountId}>
                      {account.accLoginName} - {account.shortDescription || 'No description'}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              {/* Select By Symbol */}
              <div className="flex items-center gap-2">
                <label className="text-dark-300 text-sm font-medium">Symbol:</label>
                <select
                  value={selectedSymbolForUsers}
                  onChange={async (e) => { setSelectedSymbolForUsers(e.target.value); await loadUsersBySymbol(e.target.value); }}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm min-w-[220px]"
                >
                  <option value="">Select a symbol...</option>
                  {symbolModeList.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-primary-600 text-white px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base w-full sm:w-auto"
          >
            + Add Symbol
          </button>
          {selectedSymbols.length > 0 && (
            <button
              onClick={() => handleBulkAction('delete')}
              className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
            >
              Delete ({selectedSymbols.length})
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-dark-300 font-medium mb-2 text-sm">Search Symbols</label>
            <input
              type="text"
              placeholder="Search by symbol name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-dark-300 font-medium mb-2 text-sm">Filter by Strategy</label>
            <select
              value={filterStrategy}
              onChange={(e) => setFilterStrategy(e.target.value)}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm"
            >
              <option value="all">All Strategies</option>
              <option value="1">HHLL</option>
              <option value="2">Super Trend</option>
              <option value="3">Manual</option>
              <option value="4">EMA</option>
              <option value="5">Buy Sell</option>
            </select>
          </div>
          <div>
            <label className="block text-dark-300 font-medium mb-2 text-sm">Filter by Market</label>
            <select
              value={filterMarket}
              onChange={(e) => setFilterMarket(e.target.value)}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm"
            >
              <option value="all">All Markets</option>
              <option value="1">Binance</option>
              <option value="2">FXCM</option>
              <option value="3">MT5</option>
              <option value="4">cTrader</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStrategy('all');
                setFilterMarket('all');
              }}
              className="w-full bg-dark-700 text-dark-200 px-3 py-2 rounded-lg text-sm font-medium hover:bg-dark-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
          <div className="flex justify-between items-center mb-4 sm:p-5">
            <h4 className="text-lg sm:text-xl font-semibold text-white">
              {editingSymbol ? 'Edit Symbol' : 'Add New Symbol'}
            </h4>
            {editingSymbol && (
              <p className="text-blue-300 text-sm">
                Editing symbol: {editingSymbol.symbolName} (User: {getUserName(editingSymbol.userId)})
              </p>
            )}
            <button onClick={resetForm} className="text-dark-400 hover:text-white transition-colors">✕</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* User and Trading Account Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Select User *
                </label>
                <select
                  name="userId"
                  value={formData.userId}
                  onChange={handleInputChange}
                  required
                  disabled={editingSymbol}
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select a user...</option>
                  {users.map((user) => {
                    console.log('Rendering user option:', user);
                    return (
                      <option key={user.userId} value={user.userId}>
                        {user.userFirstName} {user.userLastName} ({user.eMailId})
                      </option>
                    );
                  })}
                </select>
                {users.length === 0 && (
                  <p className="text-yellow-400 text-sm mt-1">Loading users...</p>
                )}
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Select Trading Account *
                </label>
                <select
                  name="accountId"
                  value={formData.accountId}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.userId || editingSymbol}
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!formData.userId ? 'Select a user first...' : 'Select a trading account...'}
                  </option>
                  {tradingAccounts.map((account) => (
                    <option key={account.accountId} value={account.accountId}>
                      {account.accLoginName} - {account.shortDescription || 'No description'}
                    </option>
                  ))}
                </select>
                {formData.userId && tradingAccounts.length === 0 && (
                  <p className="text-yellow-400 text-sm mt-1">Loading trading accounts...</p>
                )}
                <p className="text-dark-400 text-xs mt-1">
                  Users: {users.length} | Accounts: {tradingAccounts.length}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Symbol Name *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="symbolName"
                    value={formData.symbolName}
                    onChange={handleInputChange}
                    placeholder="e.g., BTCUSDT"
                    required
                    className="flex-1 px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!formData.symbolName) {
                        setNotice({ open: true, type: 'info', title: 'Notice', message: 'Enter a symbol to fetch details.' });
                        return;
                      }
                      try {
                        const res = await (await import('../services/api')).default.getSymbolLimits({ symbol: formData.symbolName, accountId: formData.accountId });
                        if (res && res.errorCode === 200) {
                          let minNotionalUSDT = null;
                          try {
                            const sym = formData.symbolName.toUpperCase();
                            const resp = await fetch(`https://fapi.binance.com/fapi/v1/ticker/price?symbol=${encodeURIComponent(sym)}`);
                            const data = await resp.json();
                            const price = parseFloat(data.price);
                            if (!isNaN(price) && res.minQty != null) {
                              minNotionalUSDT = Number(res.minQty) * price;
                            }
                          } catch (e) { /* ignore */ }
                          setLimits({ minQty: res.minQty, maxLeverage: res.maxLeverage, minNotionalUSDT });
                          const usdtTxt = minNotionalUSDT != null ? `$${minNotionalUSDT.toFixed(2)}` : '-';
                          setNotice({ open: true, type: 'success', title: 'Symbol Details', message: `Min Size (USDT): ${usdtTxt} | Max Leverage: ${res.maxLeverage ?? '-'}` });
                        } else {
                          setNotice({ open: true, type: 'error', title: 'Error', message: res?.errorMessage || 'Failed to fetch symbol details.' });
                        }
                      } catch (e) {
                        setNotice({ open: true, type: 'error', title: 'Error', message: e.message || 'Failed to fetch symbol details.' });
                      }
                    }}
                    className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
                  >
                    Get Details
                  </button>
                </div>
                {limits && (
                  <p className="text-xs text-dark-300 mt-2">Min Size (USDT): {limits.minNotionalUSDT != null ? `$${limits.minNotionalUSDT.toFixed(2)}` : '-'} | Max Leverage: {limits.maxLeverage ?? '-'}</p>
                )}
              </div>

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Strategy *</label>
                <select
                  name="strategyId"
                  value={formData.strategyId}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value={1}>HHLL</option>
                  <option value={2}>Super Trend</option>
                  <option value={3}>Manual</option>
                  <option value={4}>EMA</option>
                  <option value={5}>Buy Sell</option>
                </select>
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Market *</label>
                <select
                  name="market"
                  value={formData.market}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value={1}>Binance</option>
                  <option value={2}>FXCM</option>
                  <option value={3}>MT5</option>
                  <option value={4}>cTrader</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Initial Size (USDT) *</label>
                <input
                  type="number"
                  name="initialSizeInUsdt"
                  value={formData.initialSizeInUsdt}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Leverage *</label>
                <input
                  type="number"
                  name="leverage"
                  value={formData.leverage}
                  onChange={handleInputChange}
                  required
                  min="1"
                  max="10000"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Start Time (Hour) *</label>
                <input
                  type="number"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  required
                  min="0"
                  max="23"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">End Time (Hour) *</label>
                <input
                  type="number"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  required
                  min="0"
                  max="23"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">First Take Profit (%) *</label>
                <input
                  type="number"
                  name="firstTakeProfitPer"
                  value={formData.firstTakeProfitPer}
                  onChange={handleInputChange}
                  required
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Second Take Profit (%) *</label>
                <input
                  type="number"
                  name="secondTakeProfitPer"
                  value={formData.secondTakeProfitPer}
                  onChange={handleInputChange}
                  required
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Stop Loss (%) *</label>
                <input
                  type="number"
                  name="stopLoss"
                  value={formData.stopLoss}
                  onChange={handleInputChange}
                  required
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Environment *</label>
                <select
                  name="env"
                  value={formData.env}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value="T">Test</option>
                  <option value="L">Live</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Max Lot *</label>
                <input
                  type="number"
                  name="maxLot"
                  value={formData.maxLot}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Forex Type *</label>
                <select
                  name="forexType"
                  value={formData.forexType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value={1}>Forex</option>
                  <option value={2}>Gold</option>
                  <option value={3}>Crypto</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-primary-600 text-white px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : editingSymbol ? 'Update Symbol' : 'Create Symbol'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-dark-700 text-dark-200 px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-dark-600 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Symbols List / Users for Symbol */}
      <div className="bg-dark-800 border border-dark-700 rounded-xl overflow-hidden shadow-lg">
        <div className="p-4 sm:p-6 border-b border-dark-700">
          <h4 className="text-lg sm:text-xl font-semibold text-white">{viewMode==='symbol' ? 'Users for Selected Symbol' : 'Symbols List'}</h4>
        </div>

        {viewMode==='symbol' ? (
          selectedSymbolForUsers && usersForSymbol.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-dark-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">User</th>
                    <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Email</th>
                    <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Role</th>
                    <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {usersForSymbol.map(u => (
                    <tr key={u.userId} className="border-b border-dark-700">
                      <td className="px-4 py-3 text-sm text-white">{u.userFirstName} {u.userLastName}</td>
                      <td className="px-4 py-3 text-sm text-white">{u.eMailId}</td>
                      <td className="px-4 py-3 text-sm text-white">{u.userRole===3?'Admin':u.userRole===2?'Trader / Manager':'Trader'}</td>
                      <td className="px-4 py-3 text-sm text-white">{u.status===1?'Active':u.status===2?'Frozen':'Suspended'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-dark-400">{selectedSymbolForUsers ? 'No users found for this symbol' : 'Select a symbol to view users'}</p>
            </div>
          )
        ) : !selectedAccountId ? (
          <div className="p-8 text-center">
            <p className="text-dark-400">Select a trading account to view symbols</p>
          </div>
        ) : loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-dark-400">Loading symbols...</p>
          </div>
        ) : filteredSymbols.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-dark-400">No symbols found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">
                    <input
                      type="checkbox"
                      checked={selectedSymbols.length === filteredSymbols.length && filteredSymbols.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Account ID</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Strategy</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Market</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Size (USDT)</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Leverage</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Time</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Take Profit</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Stop Loss</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Environment</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Max Lot</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Forex Type</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSymbols.map((symbol) => (
                  <tr key={symbol.userSymbolId || `${symbol.userId}-${symbol.symbolName}-${symbol.accountId}`} className="border-b border-dark-700 hover:bg-dark-700/50 transition-colors">
                    <td className="px-4 py-3 text-sm sm:text-base">
                      <input
                        type="checkbox"
                        checked={selectedSymbols.includes(symbol.userSymbolId)}
                        onChange={() => handleSelectSymbol(symbol.userSymbolId)}
                        className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-white font-medium">
                      {symbol.symbolName}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-white">
                      {symbol.accountId}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base">
                      <span className={getStrategyColor(symbol.strategyId)}>
                        {getStrategyName(symbol.strategyId)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-white">
                      {getMarketName(symbol.market)}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-green-400">
                      ${symbol.initialSizeInUsdt?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-blue-400">
                      {symbol.leverage}x
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-yellow-400">
                      {symbol.startTime}:00 - {symbol.endTime}:00
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-purple-400">
                      {symbol.firstTakeProfitPer}% / {symbol.secondTakeProfitPer}%
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-red-400">
                      {symbol.stopLoss}%
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-blue-400">
                      {symbol.env === 'T' ? 'Test' : 'Live'}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-green-400">
                      {symbol.maxLot}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-orange-400">
                      {symbol.forexType === 1 ? 'Forex' : symbol.forexType === 2 ? 'Gold' : 'Crypto'}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(symbol)}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                          title="Edit Symbol"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(symbol)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title="Delete Symbol"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredSymbols.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-dark-400">No symbols found matching your criteria</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-4">Confirm Delete</h3>
            <p className="text-dark-300 mb-6">
              Are you sure you want to delete symbol "{symbolToDelete?.symbolName}"? 
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete Symbol'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSymbolToDelete(null);
                }}
                className="bg-dark-700 text-dark-200 px-4 py-2 rounded-lg font-medium hover:bg-dark-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Symbols;
